import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag, Layers, Percent, Gift, ChevronRight } from 'lucide-react';
import type { DiscountResponse, DiscountType } from '@/features/discounts/types';
import { usePublicDiscounts } from '@/features/storefront/hooks/usePublicDiscounts';

interface PromoBannerProps {
  restaurantId: string;
  currency?: string;
}

const TYPE_ICON: Record<DiscountType, React.ComponentType<{ className?: string }>> = {
  PERCENTAGE:   Percent,
  FIXED_AMOUNT: Tag,
  FLAT_RATE:    Tag,
  BUY_X_GET_Y:  Gift,
  COMBO:        Layers,
};

interface DiscountChip {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  name: string;
  badge: string;
  sub?: string;
}

const PromoBanner: React.FC<PromoBannerProps> = ({ restaurantId, currency = 'EUR' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: discounts = [], isPending } = usePublicDiscounts(restaurantId);

  // Reserve space while loading so the page doesn't shift when discounts arrive
  if (isPending) {
    return <div className="w-full h-[29px] bg-emerald-700" aria-hidden="true" />;
  }

  const activeDiscounts = discounts.filter((d: DiscountResponse) => d.isActive);
  if (activeDiscounts.length === 0) return null;

  const symbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';

  const chips: DiscountChip[] = activeDiscounts.map((d: DiscountResponse) => {
    const icon = TYPE_ICON[d.discountType] ?? Tag;

    const timeRange = d.startTime && d.endTime
      ? `${d.startTime.slice(0, 5)}–${d.endTime.slice(0, 5)}`
      : null;

    const comboContents = d.comboItems && d.comboItems.length > 0
      ? d.comboItems
          .map((ci) => ci.categoryName ?? ci.menuItemName)
          .filter(Boolean)
          .join(' + ')
      : null;

    switch (d.discountType) {
      case 'PERCENTAGE':
        return { key: d.discountId, icon, name: d.name, badge: `${d.discountValue}% OFF`, sub: timeRange ?? t('promoBanner.saveNow') };
      case 'FIXED_AMOUNT':
        return { key: d.discountId, icon, name: d.name, badge: `${symbol}${d.discountValue} OFF`, sub: timeRange ?? t('promoBanner.limitedOffer') };
      case 'FLAT_RATE':
        return { key: d.discountId, icon, name: d.name, badge: `${symbol}${d.discountValue} flat`, sub: timeRange ?? t('promoBanner.flatRateDeal') };
      case 'COMBO':
        return { key: d.discountId, icon, name: d.name, badge: `${symbol}${d.comboPrice}`, sub: comboContents ?? t('promoBanner.comboDeal') };
      case 'BUY_X_GET_Y':
        return {
          key: d.discountId, icon, name: d.name,
          badge: d.buyXGetY ? `Buy ${d.buyXGetY.buyQuantity} Get ${d.buyXGetY.getQuantity}` : d.name,
          sub: timeRange ?? t('promoBanner.specialOffer'),
        };
      default:
        return { key: d.discountId, icon, name: d.name, badge: d.name, sub: timeRange ?? t('promoBanner.specialOffer') };
    }
  });

  // Two copies — animation moves exactly -50% so the loop is perfectly seamless.
  // Chips are duplicated further if there are very few discounts to guarantee
  // the track is always wider than the viewport.
  const baseChips = chips.length < 4 ? [...chips, ...chips, ...chips, ...chips] : [...chips, ...chips];
  const marqueeChips = [...baseChips, ...baseChips];

  return (
    <div className="w-full relative overflow-hidden bg-emerald-700">
      {/* Subtle inner highlight at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-white/20 pointer-events-none" aria-hidden="true" />

      {/* Left fade */}
      <div className="absolute left-0 inset-y-0 w-14 bg-gradient-to-r from-emerald-700 to-transparent z-10 pointer-events-none" aria-hidden="true" />

      {/* Right: "View all" */}
      <div className="absolute right-0 inset-y-0 z-10 flex items-center bg-gradient-to-l from-emerald-700 via-emerald-700/95 to-transparent pl-10 pr-3">
        <button
          onClick={() => navigate(`/${restaurantId}/menu`)}
          className="flex items-center gap-0.5 text-white/60 hover:text-white transition-colors duration-200 group"
        >
          <span className="text-[10px] font-semibold tracking-wide uppercase">{t('promoBanner.viewOffers')}</span>
          <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform duration-200" />
        </button>
      </div>

      {/* Marquee */}
      <div className="flex animate-promo-marquee w-max py-1.5">
        {marqueeChips.map((chip, index) => {
          const Icon = chip.icon;
          return (
            <React.Fragment key={index}>
              <button
                onClick={() => navigate(`/${restaurantId}/menu`)}
                className="flex items-center gap-2.5 mx-4 shrink-0 group"
              >
                {/* Offer tag badge */}
                <div className="flex items-center gap-1 bg-white/15 group-hover:bg-white/25 transition-colors rounded-full px-2 py-0.5 shrink-0">
                  <Icon className="w-2.5 h-2.5 text-white/90 shrink-0" />
                  <span className="text-[10px] font-bold text-white leading-none whitespace-nowrap">
                    {chip.badge}
                  </span>
                </div>

                {/* Name */}
                <span className="text-[11px] font-medium text-white/75 group-hover:text-white transition-colors whitespace-nowrap">
                  {chip.name}
                </span>

                {/* Sub label */}
                {chip.sub && (
                  <span className="text-[10px] text-white/40 group-hover:text-white/60 transition-colors whitespace-nowrap hidden sm:inline">
                    {chip.sub}
                  </span>
                )}
              </button>

              {/* Separator */}
              <span className="text-white/20 self-center shrink-0 text-xs select-none">✦</span>
            </React.Fragment>
          );
        })}
      </div>

      <style>{`
        @keyframes promo-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .animate-promo-marquee {
          animation: promo-marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default PromoBanner;
